import { useState, useMemo, useEffect } from 'react';
import { Outlet, NavLink, Navigate } from 'react-router-dom';
import { useAdmin } from '@/hooks/useAdmin';
import BouncingLogoLoader, { usePreloaderVariant } from '@/components/BouncingLogoLoader';
import {
  LayoutDashboard, FileText, FolderOpen, Tag, Image as ImageIcon, LogOut, Inbox, Megaphone,
  Mail, Languages, Activity, Scroll, MessageSquare, Home, Palette, Handshake, FileQuestion,
  BarChart3, Settings, Rss, Vote, Bell, LayoutTemplate, Zap, Radio, Activity as LiveIcon,
  BookOpen, Sparkles, ChevronDown, ChevronRight, Search, Users, Heart, Percent, Ticket,
  CreditCard, Receipt, DollarSign, Coins, Gift, TrendingUp, Briefcase, Store, BarChart,
  LineChart, FlaskConical, Compass, MapPin, Waypoints, Wand2, Globe2, FileBarChart, Database,
  Share2, CalendarClock, ShieldCheck, Book, History, UserCheck, Link2, AlertTriangle, FileCheck,
  Film, Map as MapIcon, Bitcoin, Eye, Camera, LayoutList, UsersRound, HelpCircle, ListChecks, ListOrdered,
  Calendar, Copy, ShieldAlert, Menu as MenuIcon, X as XIcon,
} from 'lucide-react';

/**
 * Grouped admin menu — mirrors the structure in the product screenshots.
 * Items without a concrete route fall back to a shared /admin/coming-soon
 * placeholder route (implemented in App.js) to keep the menu complete while
 * the corresponding module is scheduled for development.
 */
const SECTIONS = [
  {
    key: 'manage', label: 'Upravljanje', items: [
      { to: '/admin', label: 'Nadzorna ploča', icon: LayoutDashboard, end: true },
      { to: '/admin/analytics-live', label: 'Analitika uživo', icon: LiveIcon },
      { to: '/admin/ai-content', label: 'Obrada sadržaja uz AI', icon: Sparkles },
      { to: '/admin/homepage-settings', label: 'Postavke Hero sekcije', icon: Home },
      { to: '/admin/landing', label: 'Odredišne stranice', icon: LayoutTemplate },
      { to: '/admin/breaking-news', label: 'Vijesti u prekidu', icon: Zap },
      { to: '/admin/breaking-expiry', label: 'Istek breaking vijesti', icon: AlertTriangle },
      { to: '/admin/streams', label: 'Upravljanje streamovima', icon: Radio },
    ]
  },
  {
    key: 'content', label: 'Sadržaj', items: [
      { to: '/admin/articles', label: 'Članci', icon: FileText },
      { to: '/admin/feeds', label: 'RSS/Web feedovi (V2)', icon: Rss },
      { to: '/admin/ai-settings', label: 'AI postavke', icon: Sparkles },
      { to: '/admin/blog', label: 'Blog', icon: BookOpen },
      { to: '/admin/sponsored', label: 'Sponzorirano', icon: Megaphone },
      { to: '/admin/rss', label: 'RSS izvori', icon: Rss },
      { to: '/admin/comments', label: 'Komentari', icon: MessageSquare },
      { to: '/admin/static-pages', label: 'Stranice', icon: Scroll },
      { to: '/admin/imprint', label: 'Impresum — Tim', icon: UsersRound },
      { to: '/admin/media', label: 'Mediji', icon: ImageIcon },
      { to: '/admin/categories', label: 'Kategorije', icon: FolderOpen },
      { to: '/admin/series', label: 'Serija članaka', icon: BookOpen },
      { to: '/admin/authors', label: 'Autori', icon: UsersRound },
      { to: '/admin/quizzes', label: 'Kvizovi', icon: HelpCircle },
    ]
  },
  {
    key: 'editorial', label: 'Uredništvo', items: [
      { to: '/admin/workflow', label: 'Uređivački tijek rada', icon: ListChecks },
      { to: '/admin/bulk-ops', label: 'Skupne operacije', icon: ListOrdered },
      { to: '/admin/content-schedule', label: 'Raspored sadržaja', icon: CalendarClock },
      { to: '/admin/content-calendar', label: 'Kalendar sadržaja', icon: Calendar },
      { to: '/admin/duplicates', label: 'Detekcija dupliciranja', icon: Copy },
      { to: '/admin/sanitize', label: 'Sanitacija sadržaja', icon: ShieldAlert },
      { to: '/admin/broken-images', label: 'Polomljene slike (AI)', icon: ImageIcon },
    ]
  },
  {
    key: 'users-subs', label: 'Korisnici i pretplate', items: [
      { to: '/admin/users', label: 'Korisnici', icon: Users },
      { to: '/admin/subscriptions', label: 'Pretplate', icon: Heart },
      { to: '/admin/promo-codes', label: 'Promotivni kodovi', icon: Percent },
      { to: '/admin/marketing-discounts', label: 'Marketing popusti', icon: Percent },
      { to: '/admin/inbox', label: 'Inbox (poruke)', icon: Inbox },
      { to: '/admin/vouchers', label: 'Bonovi / Popusti', icon: Ticket },
    ]
  },
  {
    key: 'finance', label: 'Financije', items: [
      { to: '/admin/payments', label: 'Plaćanja', icon: CreditCard },
      { to: '/admin/invoices', label: 'Računi', icon: Receipt },
      { to: '/admin/pricing', label: 'Cjenik', icon: DollarSign },
      { to: '/admin/micropayments', label: 'Mikroplaćanja', icon: Coins },
      { to: '/admin/tips', label: 'Savjeti / Donacije', icon: Gift },
      { to: '/admin/monetization', label: 'Monetizacija', icon: TrendingUp },
    ]
  },
  {
    key: 'portals', label: 'Portali', items: [
      { to: '/admin/jobs', label: 'Oglasi za posao', icon: Briefcase },
      { to: '/admin/classifieds', label: 'Oglasi', icon: Inbox },
      { to: '/admin/listing-promotion', label: 'Promocija oglasa', icon: Sparkles },
      { to: '/admin/times-posao', label: 'Times Posao portal', icon: Briefcase },
      { to: '/admin/sponsored-market', label: 'Sponzorirano tržište', icon: Store },
    ]
  },
  {
    key: 'analytics-ai', label: 'Analitika i AI', items: [
      { to: '/admin/analytics', label: 'Analitika', icon: BarChart3 },
      { to: '/admin/advanced-analytics', label: 'Napredna analitika', icon: LineChart },
      { to: '/admin/ab-testing', label: 'A/B testiranje', icon: FlaskConical },
      { to: '/admin/heatmap', label: 'Toplinska mapa čitanja', icon: Activity },
      { to: '/admin/visitor-map', label: 'Karta posjetitelja', icon: MapPin },
      { to: '/admin/ai-recommendations', label: 'Preporuke sadržaja', icon: Sparkles },
      { to: '/admin/performance-forecast', label: 'Prognoza izvedbe', icon: TrendingUp },
      { to: '/admin/competitor-tracking', label: 'Praćenje konkurencije', icon: Globe2 },
      { to: '/admin/churn-prediction', label: 'Predviđanje odlazaka', icon: AlertTriangle },
    ]
  },
  {
    key: 'seo', label: 'SEO i distribucija', items: [
      { to: '/admin/seo-dashboard', label: 'SEO nadzorna ploča', icon: BarChart },
      { to: '/admin/content-health', label: 'Zdravlje sadržaja', icon: Activity },
      { to: '/admin/amp-discover', label: 'AMP i Google Discover', icon: Compass },
      { to: '/admin/newsletter-digest', label: 'Sažetak newslettera', icon: Mail },
      { to: '/admin/weekly-reports', label: 'Tjedni izvještaji', icon: FileBarChart },
      { to: '/admin/syndication', label: 'Syndication API', icon: Share2 },
    ]
  },
  {
    key: 'marketing', label: 'Marketing', items: [
      { to: '/admin/newsletter', label: 'Newsletter', icon: Mail },
      { to: '/admin/email-campaigns', label: 'Email kampanje', icon: Mail },
      { to: '/admin/push', label: 'Push obavijesti', icon: Bell },
      { to: '/admin/social', label: 'Društvene mreže', icon: Share2 },
      { to: '/admin/social-schedule', label: 'Raspored društvenih mreža', icon: CalendarClock },
      { to: '/admin/ads', label: 'Upravljanje oglasima', icon: Megaphone },
      { to: '/admin/ad-reports', label: 'Izvješće o oglasima', icon: FileBarChart },
      { to: '/admin/adsense', label: 'Postavke AdSensea', icon: Wand2 },
      { to: '/admin/advertise-analytics', label: 'Analitika oglašavanja', icon: LineChart },
    ]
  },
  {
    key: 'system', label: 'Sustav', items: [
      { to: '/admin/translate-jobs', label: 'Prijevodi', icon: Languages },
      { to: '/admin/polls', label: 'Ankete', icon: Vote },
      { to: '/admin/chat-sessions', label: 'Sesije čavrljanja', icon: MessageSquare },
      { to: '/admin/documentation', label: 'Dokumentacija', icon: Book },
      { to: '/admin/import/wordpress', label: 'Uvoz', icon: Database },
      { to: '/admin/content-reports', label: 'Izvještaji o sadržaju', icon: AlertTriangle },
      { to: '/admin/audit-log', label: 'Revizijski zapis', icon: History },
      { to: '/admin/observability', label: 'Zdravlje sustava (legacy)', icon: Activity },
      { to: '/admin/system-health', label: 'Zdravlje sustava uživo', icon: LiveIcon },
      { to: '/admin/security', label: 'Sigurnost', icon: ShieldCheck },
      { to: '/admin/settings', label: 'Postavke', icon: Settings },
      { to: '/admin/branding', label: 'Rebrendiranje', icon: Palette },
      { to: '/admin/widgets', label: 'Widgeti / Katalozi', icon: LayoutList },
      { to: '/admin/times-brands', label: 'Times Brendovi', icon: Megaphone },
      { to: '/admin/county-map', label: 'Karta županija', icon: MapIcon },
      { to: '/admin/crypto-widget', label: 'Kripto widget', icon: Bitcoin },
      { to: '/admin/crypto-tips', label: 'Kripto napojnice', icon: Bitcoin },
      { to: '/admin/portal-manage', label: 'Upravljanje portalom', icon: Eye },
      { to: '/admin/doctor-revenue', label: 'Zarada Doktora', icon: TrendingUp },
      { to: '/admin/photo-check', label: 'Provjera fotografija', icon: Camera },
      { to: '/admin/menu-manager', label: 'Upravitelj izbornika', icon: LayoutList },
      { to: '/admin/sidebar-editor', label: 'Uredi admin izbornik', icon: Settings },
      { to: '/admin/version-history', label: 'Povijest verzija', icon: History },
      { to: '/admin/citizen-content', label: 'Građanski sadržaj', icon: UserCheck },
      { to: '/admin/partners', label: 'Partnerski linkovi', icon: Link2 },
      { to: '/admin/database', label: 'Baza podataka', icon: Database },
      { to: '/admin/complaints', label: 'Tužibaba prijave', icon: AlertTriangle },
      { to: '/admin/offer-generator', label: 'Ponuda generator', icon: FileCheck },
      { to: '/admin/footer-partners', label: 'Footer partneri', icon: Link2 },
      { to: '/admin/polls-analytics', label: 'Ankete / Polls', icon: BarChart },
    ]
  },
  {
    key: 'legacy', label: 'Ostalo', items: [
      { to: '/admin/news-submissions', label: 'Predane vijesti', icon: Inbox },
      { to: '/admin/ad-inquiries', label: 'Upiti za oglase', icon: Megaphone },
      { to: '/admin/contact-messages', label: 'Kontakt poruke', icon: MessageSquare },
      { to: '/admin/bug-reports', label: 'Prijavljeni bugovi', icon: AlertTriangle },
      { to: '/admin/email-log', label: 'Email zapis', icon: Mail },
      { to: '/admin/translations/missing', label: 'Neprevedeni sadržaj', icon: FileQuestion },
      { to: '/admin/i18n-coverage', label: 'Pokrivenost prijevoda', icon: Languages },
      { to: '/admin/tags', label: 'Oznake', icon: Tag },
    ]
  },
];


function Section({ section, query, openKey, setOpenKey }) {
  const match = (it) => !query || it.label.toLowerCase().includes(query.toLowerCase());
  const visible = section.items.filter(match);
  if (!visible.length) return null;
  const open = openKey === section.key || !!query;
  return (
    <div className="mb-0.5">
      <button type="button"
              onClick={() => setOpenKey(k => k === section.key ? null : section.key)}
              data-testid={`nav-section-${section.key}`}
              className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-300">
        <span>{section.label}</span>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {open && (
        <div className="space-y-0.5">
          {visible.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
                     className={({ isActive }) =>
                       `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                         isActive ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
                       }`
                     }
                     data-testid={`nav-${to.split('/').pop() || 'dashboard'}`}>
              <Icon size={14} className="shrink-0" />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}


export default function AdminLayout() {
  const { user, isAdmin, isAuthenticated, isLoading, logout, authFetch } = useAdmin();
  const preloaderVariant = usePreloaderVariant();
  const [openKey, setOpenKey] = useState('manage');
  const [query, setQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cfg, setCfg] = useState({ overrides: {}, order: [] });

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    (async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/admin-sidebar-config`);
        if (r.ok) {
          const d = await r.json();
          setCfg({ overrides: d.overrides || {}, order: d.order || [] });
        }
      } catch {}
    })();
  }, [isAuthenticated, isAdmin, authFetch]);

  const sections = useMemo(() => {
    const { overrides, order } = cfg;
    const applyOverrides = (items) => items
      .map(it => {
        const ov = overrides[it.to] || {};
        return { ...it, label: ov.label || it.label, __hidden: !!ov.hidden };
      })
      .filter(it => !it.__hidden);
    // Apply custom order within each section (by `to`). Items not in `order`
    // keep their default position, appended after the ordered ones in original
    // sequence.
    const applyOrder = (items) => {
      if (!order || order.length === 0) return items;
      const orderIdx = new Map(order.map((p, i) => [p, i]));
      return [...items].sort((a, b) => {
        const ai = orderIdx.has(a.to) ? orderIdx.get(a.to) : 1e6;
        const bi = orderIdx.has(b.to) ? orderIdx.get(b.to) : 1e6;
        if (ai !== bi) return ai - bi;
        return 0;
      });
    };
    return SECTIONS.map(s => ({ ...s, items: applyOrder(applyOverrides(s.items)) }))
      .filter(s => s.items.length > 0);
  }, [cfg]);

  if (isLoading) {
    return (
      <div className="h-screen bg-zinc-950" data-testid="admin-loading">
        <BouncingLogoLoader variant={preloaderVariant} fullscreen label="Učitavanje admin panela…" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="flex h-screen bg-zinc-950 text-zinc-100 overflow-hidden" data-testid="admin-layout">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-3 py-2.5 bg-zinc-950 border-b border-zinc-800">
        <button onClick={() => setMobileOpen(true)} data-testid="admin-mobile-menu-btn"
                className="p-2 rounded hover:bg-zinc-800 text-zinc-300">
          <MenuIcon size={20} />
        </button>
        <h1 className="font-serif text-base tracking-tight">e-mina CMS</h1>
        <button onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                data-testid="admin-mobile-logout-btn"
                className="p-2 rounded hover:bg-zinc-800 text-zinc-400">
          <LogOut size={18} />
        </button>
      </div>

      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)} data-testid="admin-mobile-backdrop"
             className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" />
      )}

      <aside className={`${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-72 lg:w-64 border-r border-zinc-800 flex flex-col shrink-0 bg-zinc-950 transition-transform duration-200 ease-out`}>
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-xl tracking-tight">e-mina CMS</h1>
            <span className="text-xs text-zinc-500">Admin panel</span>
          </div>
          <button onClick={() => setMobileOpen(false)} data-testid="admin-mobile-close-btn"
                  className="lg:hidden p-1.5 rounded hover:bg-zinc-800 text-zinc-400">
            <XIcon size={18} />
          </button>
        </div>
        <div className="p-3 border-b border-zinc-800">
          <label className="relative block">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <input value={query} onChange={e => setQuery(e.target.value)}
                   placeholder="Pretraži admin…"
                   data-testid="admin-nav-search"
                   className="w-full bg-zinc-900 border border-zinc-800 rounded-md pl-8 pr-2 py-1.5 text-xs placeholder:text-zinc-600 focus:border-red-500 outline-none" />
          </label>
        </div>
        <nav className="flex-1 overflow-y-auto p-2" onClick={(e) => {
          // Auto-close mobile drawer when nav link clicked
          if (e.target.closest('a')) setMobileOpen(false);
        }}>
          {sections.map(s => <Section key={s.key} section={s} query={query} openKey={openKey} setOpenKey={setOpenKey} />)}
        </nav>
        <div className="p-3 border-t border-zinc-800">
          <div className="flex items-center gap-2 mb-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-zinc-700" />
            )}
            <div className="min-w-0">
              <p className="text-xs font-medium truncate">{user?.name || user?.email}</p>
              <p className="text-[10px] text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                  className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                  data-testid="admin-logout-btn">
            <LogOut size={12} /> Odjava
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto pt-12 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
